import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API_URL } from '../config/api';
import LoadingIndicator from '../Components/LoadingIndicator';

const ContentView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState(null);
  const [productImageViewUrls, setProductImageViewUrls] = useState([]);
  const [isLoadingImage, setIsLoadingImage] = useState(false);

  const productImageKeys = useMemo(() => {
    if (!data) return [];
    const fromProfile = Array.isArray(data.profileImageUrl)
      ? data.profileImageUrl.filter((k) => k && String(k).trim())
      : [];
    if (fromProfile.length) {
      return [...new Set(fromProfile)];
    }
    if (data.productImageKey) {
      return [data.productImageKey];
    }
    return [];
  }, [data]);

  const formatDateTime = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString('en-IN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    }).replace(',', ' ');
  };

  const formatDate = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  const formatAmount = (value) => {
    if (value === null || value === undefined) return '-';
    return parseFloat(value).toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };
    const getCategoryLabel = (data) => {
  if (!data.category) return '-';
  if (typeof data.category === 'object') return data.category.category || '-';
  return data.category;
};


  const formatStatus = (value) => {
    if (!value) return '-';
    const text = String(value).toLowerCase();
    return `${text.charAt(0).toUpperCase()}${text.slice(1)}`;
  };

  const getStatusTextClass = (label) => {
    const normalized = String(label || '').toLowerCase();
    if (normalized.includes('active') && !normalized.includes('inactive')) {
      return 'profile-value status-text status-active';
    }
    return 'profile-value status-text';
  };

  const createdByName = useMemo(() => {
    if (!data?.createdBy) return '-';
    if (typeof data.createdBy === 'string') return data.createdBy;
    return data.createdBy.name || data.createdBy.email || data.createdBy._id || '-';
  }, [data]);

  const updatedByName = useMemo(() => {
    if (!data?.updatedBy) return '-';
    if (typeof data.updatedBy === 'string') return data.updatedBy;
    return data.updatedBy.name || data.updatedBy.email || data.updatedBy._id || '-';
  }, [data]);

  useEffect(() => {
    const fetchContent = async () => {
      if (!API_URL) {
        toast.error('API URL not configured.');
        return;
      }
      if (!id) {
        toast.error('Content ID missing.');
        return;
      }

      setIsLoading(true);
      try {
        const response = await axios.post(API_URL, {
          action: 'getContent',
          contentId: id
        }, {
          headers: { 'Content-Type': 'application/json' }
        });

        setData(response.data || null);
      } catch (error) {
        const message = error.response?.data?.message || error.message || 'Failed to load content';
        toast.error(message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchContent();
  }, [id]);

  useEffect(() => {
    let cancelled = false;

    const fetchProductImageUrls = async () => {
      if (!API_URL || !productImageKeys.length) {
        setProductImageViewUrls([]);
        setIsLoadingImage(false);
        return;
      }

      setIsLoadingImage(true);
      try {
        const urls = await Promise.all(
          productImageKeys.map(async (productImageKey) => {
            try {
              const response = await axios.post(
                API_URL,
                { action: 'getProductImageUrl', productImageKey },
                { headers: { 'Content-Type': 'application/json' } }
              );
              return response.data?.viewUrl || '';
            } catch (error) {
              console.error('Failed to load product image:', productImageKey, error);
              return '';
            }
          })
        );
        if (!cancelled) {
          setProductImageViewUrls(urls.filter(Boolean));
        }
      } finally {
        if (!cancelled) {
          setIsLoadingImage(false);
        }
      }
    };

    fetchProductImageUrls();
    return () => {
      cancelled = true;
    };
  }, [productImageKeys]);

  return (
    <div className="employee-form">
      <div className="content-header">
        <div className="row align-items-center">
          <div className="col-sm-6">
            <h1>Lead Post Details</h1>
            <p>View lead post details.</p>
          </div>
          <div className="col-sm-6 text-right subscription-header-actions">
            <button type="button" className="btn btn-secondary" onClick={() => navigate(-1)}>
              Back
            </button>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="employee-loading">
          <LoadingIndicator size={120} />
        </div>
      ) : !data ? (
        <div className="card employee-content-card">
          <div className="card-body">
            <div className="employee-empty">No content data found.</div>
          </div>
        </div>
      ) : (
        <>
          {/* Main Info Section */}
          <div className="detail-section">
            <div className="detail-section-header">
              <i className="fas fa-info-circle"></i>
              <h3>RFQ Information</h3>
            </div>
            <div className="detail-grid">
              <div className="detail-item">
                <div className="detail-icon">
                  <i className="fas fa-layer-group"></i>
                </div>
                <div className="detail-content">
                  <label>Category</label>
                  <span className="detail-value">{getCategoryLabel(data)}</span>
                </div>
              </div>
              <div className="detail-item">
                <div className="detail-icon">
                  <i className="fas fa-file-alt"></i>
                </div>
                <div className="detail-content">
                  <label>RFQ No</label>
                  <span className="detail-value highlight">{data.rfqNo || '-'}</span>
                </div>
              </div>
              <div className="detail-item">
                <div className="detail-icon">
                  <i className="fas fa-calendar"></i>
                </div>
                <div className="detail-content">
                  <label>RFQ Date</label>
                  <span className="detail-value">{formatDate(data.rfqDate)}</span>
                </div>
              </div>
              <div className="detail-item">
                <div className="detail-icon">
                  <i className="fas fa-clock"></i>
                </div>
                <div className="detail-content">
                  <label>RFQ Due Date & Time</label>
                  <span className="detail-value">{formatDateTime(data.rfqDueDateTime)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Financial Information */}
          <div className="detail-section">
            <div className="detail-section-header financial">
              <i className="fas fa-rupee-sign"></i>
              <h3>Financial Details</h3>
            </div>
            <div className="detail-grid">
              <div className="detail-item financial-item">
                <div className="detail-icon financial">
                  <i className="fas fa-money-bill-wave"></i>
                </div>
                <div className="detail-content">
                  <label>EMD Value</label>
                  <div className="financial-row">
                    <span className="detail-value amount">₹ {formatAmount(data.emdPayment)}</span>
                    <span className={`visibility-badge ${data.emdVisible ? 'visible' : 'hidden'}`}>
                      <i className={`fas fa-eye${data.emdVisible ? '' : '-slash'}`}></i>
                      {data.emdVisible ? 'Visible' : 'Hidden'}
                    </span>
                  </div>
                </div>
              </div>
              <div className="detail-item financial-item">
                <div className="detail-icon financial">
                  <i className="fas fa-receipt"></i>
                </div>
                <div className="detail-content">
                  <label>Tender Fee</label>
                  <div className="financial-row">
                    <span className="detail-value amount">₹ {formatAmount(data.tenderFee)}</span>
                    <span className={`visibility-badge ${data.tenderFeeVisible ? 'visible' : 'hidden'}`}>
                      <i className={`fas fa-eye${data.tenderFeeVisible ? '' : '-slash'}`}></i>
                      {data.tenderFeeVisible ? 'Visible' : 'Hidden'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Requirement & Contact */}
          <div className="detail-section">
            <div className="detail-section-header">
              <i className="fas fa-clipboard-list"></i>
              <h3>Requirement & Contact</h3>
            </div>
            <div className="detail-grid">
              {/* Split Summary into two separate items */}
              <div className="detail-item">
                <div className="detail-icon">
                  <i className="fas fa-align-left"></i>
                </div>
                <div className="detail-content">
                  <label>Requirement Summary</label>
                  <span className="detail-value summary">{data.requirementSummary || '-'}</span>
                </div>
              </div>
              <div className="detail-item summary-item">
                <div className="detail-icon summary-icon">
                  <i className="fas fa-file-text"></i>
                </div>
                <div className="detail-content">
                  <label>Summary</label>
                  <span className="detail-value summary">{data.summary || '-'}</span>
                </div>
              </div>
              <div className="detail-item image-item">
                <div className="detail-icon">
                  <i className="fas fa-image"></i>
                </div>
                <div className="detail-content">
                  <label>
                    Product Image
                    {productImageKeys.length > 1
                      ? ` (${productImageKeys.length})`
                      : ''}
                  </label>
                  {isLoadingImage ? (
                    <div style={{ marginTop: '10px' }}>
                      <LoadingIndicator size={40} />
                    </div>
                  ) : productImageViewUrls.length > 0 ? (
                    <div
                      className="d-flex flex-wrap"
                      style={{ marginTop: '10px', gap: '10px' }}
                    >
                      {productImageViewUrls.map((url, index) => (
                        <img
                          key={`${url}-${index}`}
                          src={url}
                          alt={`Product ${index + 1}`}
                          style={{
                            maxWidth: '100%',
                            maxHeight: '120px',
                            borderRadius: '8px',
                            border: '1px solid #e5e7eb',
                            objectFit: 'cover'
                          }}
                        />
                      ))}
                    </div>
                  ) : (
                    <span className="detail-value">No image available</span>
                  )}
                </div>
              </div>
              <div className="detail-item-group">
                <div className="detail-item">
                  <div className="detail-icon">
                    <i className="fas fa-link"></i>
                  </div>
                  <div className="detail-content">
                    <label>Reference Link</label>
                    {data.referenceLink && Array.isArray(data.referenceLink) && data.referenceLink.length > 0 ? (
                      <div className="reference-links-grid">
                        {data.referenceLink.map((link, index) => (
                          <a 
                            key={index}
                            href={link} 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className="detail-link reference-link-item"
                          >
                            <span>{link}</span>
                            <i className="fas fa-external-link-alt"></i>
                          </a>
                        ))}
                      </div>
                    ) : data.referenceLink && typeof data.referenceLink === 'string' ? (
                      <a href={data.referenceLink} target="_blank" rel="noopener noreferrer" className="detail-link">
                        <span>{data.referenceLink}</span>
                        <i className="fas fa-external-link-alt"></i>
                      </a>
                    ) : (
                      <span className="detail-value">-</span>
                    )}
                  </div>
                </div>
                <div className="detail-item">
                  <div className="detail-icon">
                    <i className="fas fa-phone"></i>
                  </div>
                  <div className="detail-content">
                    <label>Support Contact</label>
                    <span className="detail-value">{data.supportContact || '-'}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Status & Visibility */}
          <div className="detail-section">
            <div className="detail-section-header status">
              <i className="fas fa-toggle-on"></i>
              <h3>Status & Visibility</h3>
            </div>
            <div className="detail-grid">
              <div className="detail-item">
                <div className="detail-icon status">
                  <i className="fas fa-check-circle"></i>
                </div>
                <div className="detail-content">
                  <label>Status</label>
                  <span className={`status-badge ${data.status?.toLowerCase()}`}>
                    {formatStatus(data.status)}
                  </span>
                </div>
              </div>
              <div className="detail-item">
                <div className="detail-icon">
                  <i className="fas fa-users"></i>
                </div>
                <div className="detail-content">
                  <label>Lead Visibility</label>
                  <div className="visibility-tags">
                    {data.leadVisibility && data.leadVisibility.length > 0 
                      ? data.leadVisibility.map((v, i) => (
                          <span key={i} className={`visibility-tag ${v.toLowerCase()}`}>
                            <i className={`fas fa-${v.toLowerCase() === 'paid' ? 'dollar-sign' : 'gift'}`}></i>
                            {v}
                          </span>
                        ))
                      : <span className="detail-value">-</span>
                    }
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Audit Information */}
          <div className="detail-section audit">
            <div className="detail-section-header audit">
              <i className="fas fa-history"></i>
              <h3>Audit Trail</h3>
            </div>
            <div className="detail-grid">
              <div className="detail-item">
                <div className="detail-icon audit">
                  <i className="fas fa-user-plus"></i>
                </div>
                <div className="detail-content">
                  <label>Created By</label>
                  <span className="detail-value">{createdByName}</span>
                  <span className="detail-timestamp">{formatDateTime(data.createdAt)}</span>
                </div>
              </div>
              <div className="detail-item">
                <div className="detail-icon audit">
                  <i className="fas fa-user-edit"></i>
                </div>
                <div className="detail-content">
                  <label>Updated By</label>
                  <span className="detail-value">{updatedByName}</span>
                  <span className="detail-timestamp">{formatDateTime(data.updatedAt)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Action Button */}
          <div className="detail-actions">
            <button type="button" className="btn btn-primary btn-lg" onClick={() => navigate(`/content-edit/${id}`)}>
              <i className="fas fa-edit"></i>
              Edit Lead Post
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default ContentView;
